/**
 * Fight identity fallback — node10 lane, bounded (docs/bugfix/260926 §9).
 *
 * Hydracore keys Kalshi fight rows (`ufc|2026-10-10|1|Allen|Duncan`, from the
 * event title) but ships Polymarket fight rows with NO canonical key: Poly's
 * slug codes are Gamma's own (`ufc-bre1-chr20-…`) and the core has no
 * title-bound path for fight sports yet. A keyless Poly fight can never join
 * its Kalshi card, so the Poly column stays empty.
 *
 * Until the producer stamps the key, this module binds a keyless Poly fight to
 * a keyed Kalshi fight by NAME: the Poly `title` carries both full names, and
 * each Kalshi outcome row carries its fighter's full name in `subtitle`. The
 * result reuses Kalshi's own key verbatim — nothing is synthesized — and it is
 * only ever computed for a row that carries no key, so a producer stamp wins
 * the moment it arrives.
 *
 * Fail closed: both fighters must bind to the SAME Kalshi fight, and exactly
 * one Kalshi fight may qualify. Anything else returns null and the row stays
 * unpaired, exactly as before.
 */

/** One keyed Kalshi fight, as the store holds it. */
export interface KalshiFight {
  canonicalGameKey: string;
  canonicalGameKeyV2?: string;
  /** Alpha-sorted pair in the key's code space (full surnames). */
  canonicalTeams: [string, string];
  /** ISO date segment of the key. */
  date: string;
  /** code (member of `canonicalTeams`) -> that fighter's full name. */
  names: Map<string, string>;
}

export interface FightIdentity {
  canonicalGameKey: string;
  canonicalGameKeyV2?: string;
  canonicalTeams: [string, string];
  /** Kalshi code of the Poly YES fighter (the title's first-named = outcomes[0]). */
  canonicalYesTeam: string;
}

const NAME_SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv']);
const VS_SEPARATOR = /\s+vs\.?\s+/i;
const DAY_MS = 24 * 3_600_000;

/** "UFC Fight Night: A vs. B (Middleweight, Main Card)" -> ["A", "B"]. */
export function fightTitleNames(title: string | null | undefined): [string, string] | null {
  if (!title) return null;
  const colon = title.lastIndexOf(': ');
  const body = (colon >= 0 ? title.slice(colon + 2) : title).replace(/\s*\([^()]*\)\s*$/, '');
  const sides = body.split(VS_SEPARATOR).map((s) => s.trim());
  if (sides.length !== 2 || !sides[0] || !sides[1]) return null;
  return [sides[0], sides[1]];
}

/** Accent-folded, lowercased name tokens with generational suffixes dropped. */
export function fightNameTokens(name: string): string[] {
  const tokens = name
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .split(/[\s-]+/)
    .map((t) => t.replace(/[^a-z]/g, ''))
    .filter(Boolean);
  while (tokens.length > 1 && NAME_SUFFIXES.has(tokens[tokens.length - 1])) tokens.pop();
  return tokens;
}

/** Edit distance, capped: returns true when a and b differ by at most one edit. */
function withinOneEdit(a: string, b: string): boolean {
  if (a === b) return true;
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  let i = 0;
  while (i < la && i < lb && a[i] === b[i]) i += 1;
  // Same length: one substitution. Otherwise: one insertion in the longer.
  if (la === lb) return a.slice(i + 1) === b.slice(i + 1);
  return la > lb ? a.slice(i + 1) === b.slice(i) : a.slice(i) === b.slice(i + 1);
}

/** Venues transliterate differently (Daria/Darya, Zhelezniakova/Zheleznyakova):
 *  long tokens tolerate one edit; short ones must be exact. */
function tokenMatches(a: string, b: string): boolean {
  if (a === b) return true;
  return a.length >= 5 && b.length >= 5 && withinOneEdit(a, b);
}

function sharesToken(a: readonly string[], b: readonly string[]): boolean {
  return a.some((x) => b.some((y) => tokenMatches(x, y)));
}

function surnameMatches(a: readonly string[], b: readonly string[]): boolean {
  return a.length > 0 && b.length > 0 && tokenMatches(a[a.length - 1], b[b.length - 1]);
}

function dayDiff(a: string, b: string): number {
  const ta = Date.parse(`${a}T00:00:00Z`);
  const tb = Date.parse(`${b}T00:00:00Z`);
  if (!Number.isFinite(ta) || !Number.isFinite(tb)) return Number.POSITIVE_INFINITY;
  return Math.abs(ta - tb) / DAY_MS;
}

/**
 * Bind a keyless Poly fight (its title and slug date) to one keyed Kalshi
 * fight. Each Poly fighter must share a name token with a different Kalshi
 * fighter of the same fight, and at least one of the two must agree on the
 * surname — a shared first name alone never binds a fight. Kalshi keys carry
 * the ET play-date while a Poly slug freezes its creation date, so the two may
 * sit one day apart.
 */
export function deriveFightIdentity(
  polyTitle: string | null | undefined,
  polyDate: string,
  fights: readonly KalshiFight[],
): FightIdentity | null {
  const names = fightTitleNames(polyTitle);
  if (!names) return null;
  const first = fightNameTokens(names[0]);
  const second = fightNameTokens(names[1]);
  if (first.length === 0 || second.length === 0) return null;

  let found: FightIdentity | null = null;
  for (const fight of fights) {
    if (dayDiff(fight.date, polyDate) > 1) continue;
    const [c0, c1] = fight.canonicalTeams;
    const n0 = fight.names.get(c0);
    const n1 = fight.names.get(c1);
    if (!n0 || !n1) continue;
    const t0 = fightNameTokens(n0);
    const t1 = fightNameTokens(n1);
    let yes: string | null = null;
    for (const [mine, theirs, code] of [[t0, t1, c0], [t1, t0, c1]] as const) {
      // `mine` is the Kalshi fighter the Poly first-named one would bind to.
      if (!sharesToken(first, mine) || !sharesToken(second, theirs)) continue;
      if (!surnameMatches(first, mine) && !surnameMatches(second, theirs)) continue;
      // Both orientations qualifying is an ambiguous pair (same-name fighters).
      if (yes != null) return null;
      yes = code;
    }
    if (yes == null) continue;
    if (found) return null; // two Kalshi fights qualify: ambiguous, stay unpaired
    found = {
      canonicalGameKey: fight.canonicalGameKey,
      ...(fight.canonicalGameKeyV2 ? { canonicalGameKeyV2: fight.canonicalGameKeyV2 } : {}),
      canonicalTeams: fight.canonicalTeams,
      canonicalYesTeam: yes,
    };
  }
  return found;
}

/** The code among `teams` a Kalshi outcome row's `subtitle` names, or null. */
export function fightCodeForSubtitle(
  subtitle: string | null | undefined,
  teams: readonly [string, string],
): string | null {
  if (!subtitle) return null;
  const tokens = fightNameTokens(subtitle);
  const hits = teams.filter((code) => {
    const c = fightNameTokens(code).join('');
    return c !== '' && tokens.includes(c);
  });
  return hits.length === 1 ? hits[0] : null;
}
